'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChickenTickResponseKindEnum } from '../services/generated/Api';
import { useChickenTick, useClaimChicken } from '../queries/useChickenQueries';
import { RESPAWN_COOLDOWN_MS, shouldShowSpawn } from './chickenSpawnLogic';

interface ChickenSpawn {
  spawnId: string | null;
  kind: 'regular' | 'cyber';
  amount: number;
  onClaim: () => void;
  onDespawn: () => void;
}

interface ShownSpawn {
  spawnId: string;
  kind: 'regular' | 'cyber';
  amount: number;
}

export function useChickenSpawn(): ChickenSpawn {
  const { data, dataUpdatedAt } = useChickenTick();
  const claim = useClaimChicken();

  const tickSpawnId = data?.spawnId ?? null;

  // Captured once at spawn time, not re-derived from `data` on every render —
  // the tick query refetches right after a successful claim (see
  // useClaimChicken below), which used to flip kind/amount back to their
  // defaults mid-animation (#517: cyber chicken, regular feathers).
  const [shownSpawn, setShownSpawn] = useState<ShownSpawn | null>(null);
  const claimedRef = useRef<Set<string>>(new Set());
  const cooldownUntilRef = useRef(0);

  useEffect(() => {
    if (
      shouldShowSpawn({
        tickSpawnId,
        shownSpawnId: shownSpawn?.spawnId ?? null,
        claimed: claimedRef.current,
        cooldownUntil: cooldownUntilRef.current,
        now: Date.now(),
      })
    ) {
      setShownSpawn({
        spawnId: tickSpawnId as string,
        kind: data?.kind === ChickenTickResponseKindEnum.Cyber ? 'cyber' : 'regular',
        amount: data?.amount ?? 500,
      });
    }
  }, [tickSpawnId, shownSpawn?.spawnId, dataUpdatedAt, data]);

  const onClaim = useCallback(() => {
    if (shownSpawn) {
      claimedRef.current.add(shownSpawn.spawnId);
      claim.mutate(shownSpawn.spawnId);
    }
  }, [claim, shownSpawn]);

  const onDespawn = useCallback(() => {
    cooldownUntilRef.current = Date.now() + RESPAWN_COOLDOWN_MS;
    setShownSpawn(null);
  }, []);

  return {
    spawnId: shownSpawn?.spawnId ?? null,
    kind: shownSpawn?.kind ?? 'regular',
    amount: shownSpawn?.amount ?? 500,
    onClaim,
    onDespawn,
  };
}
